import type { CanvasOrientation } from "./canvas-design";

export type LocalizedText = { [locale: string]: string };

export interface VariantAxisValue {
  value: string;
  label: LocalizedText;
  sortOrder?: number;
}

export interface VariantAxis {
  key: string;
  label: LocalizedText;
  values: VariantAxisValue[];
}

/** Mirrors ProductWithMediaVariant. The generated client types it as `object`. */
export interface CanvasVariant {
  id: string;
  sku: string;
  name: LocalizedText;
  axisValues: Record<string, string>;
  printSpec: {
    widthMm: number;
    heightMm: number;
    bleedMm: number;
    safeMm?: number;
    dpi: number;
  };
  price: number;
  /** Was-price for a struck-through comparison. Null when not on offer. */
  compareAtPrice?: number | null;
  priceCurrencies?: Record<string, number>;
  shippingCostCents?: number;
  factoryPriceCents?: number;
  oversize: boolean;
  active: boolean;
  sortOrder: number;
}

const isActive = (v: CanvasVariant) => v.active !== false;

function matches(
  variant: CanvasVariant,
  axisValues: Record<string, string>,
  axes: VariantAxis[],
): boolean {
  return axes.every((a) => variant.axisValues?.[a.key] === axisValues[a.key]);
}

/**
 * The variant for an exact point in the option matrix, or null.
 *
 * Null is a normal, expected answer: the matrix is deliberately allowed to have
 * holes (a size nobody sells as a poster), and a hole is how "not sold" is
 * expressed. The caller disables rather than errors.
 */
export function resolveVariant(
  variants: CanvasVariant[],
  axisValues: Record<string, string>,
): CanvasVariant | null {
  const keys = Object.keys(axisValues ?? {});
  return (
    (variants ?? []).find(
      (v) =>
        isActive(v) &&
        v.axisValues &&
        // Every declared axis on the variant must be satisfied, and the caller
        // must have supplied all of them — a partial selection is not a match.
        Object.keys(v.axisValues).length === keys.length &&
        keys.every((k) => v.axisValues[k] === axisValues[k]),
    ) ?? null
  );
}

/**
 * Which values of `axisKey` are actually buyable, given the current selection
 * of the OTHER axes.
 *
 * This is what makes an incomplete matrix a feature instead of a support
 * ticket: a combination with no active row renders disabled in the selector,
 * so the customer never reaches add-to-cart and gets rejected there.
 */
export function sellableValues(
  variants: CanvasVariant[],
  axes: VariantAxis[],
  axisValues: Record<string, string>,
  axisKey: string,
): string[] {
  const others = axes.filter((a) => a.key !== axisKey);
  const out = new Set<string>();

  for (const variant of variants ?? []) {
    if (!isActive(variant) || !variant.axisValues) continue;
    const otherAxesAgree = others.every(
      (a) => variant.axisValues[a.key] === axisValues[a.key],
    );
    if (!otherAxesAgree) continue;
    const value = variant.axisValues[axisKey];
    if (value != null) out.add(value);
  }

  return [...out];
}

/** The cheapest-to-reason-about default: the first active variant by sortOrder. */
export function defaultAxisValues(
  variants: CanvasVariant[],
  axes: VariantAxis[],
): Record<string, string> {
  const first = (variants ?? [])
    .filter(isActive)
    .slice()
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))[0];
  if (!first) return {};

  const out: Record<string, string> = {};
  for (const axis of axes) {
    const value = first.axisValues?.[axis.key];
    if (value != null) out[axis.key] = value;
  }
  return out;
}

/** Output size of the finished piece, including the wrap zone. */
/**
 * The variant's trim size, turned to the requested orientation.
 *
 * Mirrors orientPrintSpec in the backend's canvas-geometry. Sizes are seeded
 * once, portrait, and orientation costs nothing — so the editor turns the
 * size rather than asking the shop to stock a mirrored SKU for every row.
 */
export function trimMm(
  variant: CanvasVariant,
  orientation?: CanvasOrientation,
): { width: number; height: number } {
  const { widthMm, heightMm } = variant.printSpec;
  const wantsLandscape = orientation === "landscape";
  if (!orientation || widthMm > heightMm === wantsLandscape) {
    return { width: widthMm, height: heightMm };
  }
  return { width: heightMm, height: widthMm };
}

export function outputMm(
  variant: CanvasVariant,
  orientation?: CanvasOrientation,
): {
  width: number;
  height: number;
} {
  const trim = trimMm(variant, orientation);
  const { bleedMm } = variant.printSpec;
  return {
    width: trim.width + 2 * bleedMm,
    height: trim.height + 2 * bleedMm,
  };
}

/** Aspect of the finished piece — what the crop must be shaped to. */
export function outputAspect(
  variant: CanvasVariant,
  orientation?: CanvasOrientation,
): number {
  const o = outputMm(variant, orientation);
  return o.width / o.height;
}

/**
 * Whether a size is square, and so has no portrait or landscape to choose.
 *
 * On a square the two orientation buttons produce the same picture and draw the
 * same glyph, so the control is hidden rather than shown doing nothing. A
 * sub-millimetre difference still counts as square: print specs are authored by
 * hand and 200 x 200.4 is a square in every way a customer can see.
 */
export function isSquareVariant(
  variant: { printSpec?: { widthMm?: number; heightMm?: number } } | null | undefined,
): boolean {
  const spec = variant?.printSpec;
  if (!spec?.widthMm || !spec?.heightMm) return false;
  return Math.abs(spec.widthMm - spec.heightMm) < 1;
}
